import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Activity } from '../entity/activity';
import { ActivityState } from '../entity/activitystate';

import { hashCode } from '../../shared/misc/hashcode';
import { ActivityComposite } from '../entity/activity.composite';
import { Task } from '../entity/task';
import { withFirstTeamIds } from '../misc/rxjs-operators';
import { ToastActions } from '../state/toast/toast.actions';
import { toActivtyStateTypeTransformer } from '../state/transformers/transformers';
import { ActivityStateType } from '../state/types';
import { BLService } from './bl.service';
import { TaskService } from './task.service';

interface GetActivitesByUser {
  fromDate: string;
  toDate: string;
  taskUserId: number;
  clientUserId: number;
  includeArchivedClients: boolean;
}

@Injectable({ providedIn: 'root' })
export class ActivityService extends BLService {
  private RESOURCE_ENDPOINT = 'activity';

  constructor(private taskService: TaskService) {
    super();
  }

  getActivityStates(): Observable<ActivityState[]> {
    return this.httpGet<ActivityState[]>(`${this.RESOURCE_ENDPOINT}/states`);
  }

  getActivityStatesTypes(): Observable<ActivityStateType[]> {
    return this.getActivityStates().pipe(map((states) => states.map(toActivtyStateTypeTransformer.transform)));
  }

  getActivites(
    fromDate: string,
    toDate: string,
    taskId: number,
    includeStageChanges: boolean = false,
  ): Observable<Activity[]> {
    const urlExtension = '/task/' + taskId + '/range/' + fromDate + '/' + toDate;
    const queryParam = '?include-state-changes=' + includeStageChanges;
    return this.httpGet<Activity[]>(`${this.RESOURCE_ENDPOINT}${urlExtension}${queryParam}`).pipe(
      map((activites: any[]) => activites.map((activity) => Activity.from(activity))),
    );
  }

  getActivity(date: string, taskId: number): Observable<Activity> {
    const urlExtension = '/task/' + taskId + '/date/' + date;
    return this.httpGet<Activity>(this.RESOURCE_ENDPOINT + urlExtension);
  }

  getEarliestUnfinishedDeadline(userId: number): Observable<any> {
    const urlExtension = '/earliest-unfinished-deadline/' + userId;
    return this.httpGet<any>(this.RESOURCE_ENDPOINT + urlExtension);
  }

  getActivitesByUser({
    fromDate,
    toDate,
    taskUserId,
    clientUserId,
    includeArchivedClients,
  }: GetActivitesByUser): Observable<Activity[]> {
    const queryParams = `include-archived-clients=${Number(includeArchivedClients)}`;
    const url = `${this.RESOURCE_ENDPOINT}/by-user/${fromDate}/${toDate}/${taskUserId}/${clientUserId}?${queryParams}`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpGet<Activity[]>(url, this.httpAuthHeader.getHeaderWithoutBody(ids)).pipe(
          map((response) => {
            if (response && response.length > 0) {
              return response.map((activity) => {
                return activity.id ? activity : { ...activity, unsavedId: hashCode(JSON.stringify(activity)) };
              }) as Activity[];
            }
            return response;
          }),
        ),
      ),
    );
  }

  getActivityComposite({ span, taskId }: Activity) {
    return this.getActivity(span.end, taskId).pipe(
      tap((activity: Activity | null) => {
        if (!activity) {
          throw new Error('Activity not found');
        }
      }),
      switchMap((activity: Activity) =>
        this.taskService.getTask(activity.taskId).pipe(map((task: Task) => [activity, task])),
      ),
      map(([activity, task]: [Activity, Task]) => {
        activity.task = task;
        return new ActivityComposite(task, activity);
      }),
      catchError(() => {
        this.store.dispatch(
          ToastActions.showWarnMessage({
            summary: 'Saknas',
            detail: `Aktiviteten kan inte hittas eller ett fel uppstod (taskId: ${taskId} med slutdatum ${span.end})`,
          }),
        );
        return EMPTY;
      }),
    );
  }

  saveActivity(activity: Activity): Observable<Activity> {
    const body = JSON.stringify(activity);
    const alternativeUrl = activity.statusChangeDate ? `/changed-on/${activity.statusChangeDate}` : '';
    return this.httpPut<Activity>(this.RESOURCE_ENDPOINT + alternativeUrl, body);
  }

  saveSingleActivity(date: string, taskId: number, comment: string): Observable<Activity> {
    return this.httpPut<Activity>(`${this.RESOURCE_ENDPOINT}/single-event/${date}/${taskId}?comment=${comment}`, null);
  }

  deleteActivity(activity: Activity) {
    return this.httpDelete<void>(`${this.RESOURCE_ENDPOINT}/${activity.id}`).pipe(
      tap(() =>
        this.store.dispatch(
          ToastActions.showInfoMessage({ summary: 'Borttagen', detail: 'Aktiviteten har tagits bort' }),
        ),
      ),
    );
  }
}
